import { ApiError, describeError, FormError } from '@classgo/app-kit';
import { Button, Input, Select } from '@classgo/ui';
import { type FormEvent, useState } from 'react';

import { useT } from '../i18n';
import type { StaffUserFormValues } from './types';

/**
 * Email and names, plus the role for ClassGO staff. Used to invite a school's owner and to add a
 * team member; Cognito emails the temporary password in both cases.
 */
export const PersonForm = ({
  kind,
  canCreateSuperAdmin = false,
  submitLabel,
  onSubmit,
}: {
  kind: 'owner' | 'staff';
  canCreateSuperAdmin?: boolean;
  submitLabel: string;
  onSubmit: (values: StaffUserFormValues) => Promise<void>;
}) => {
  const t = useT();
  const [values, setValues] = useState<StaffUserFormValues>({
    email: '',
    givenName: '',
    familyName: '',
    role: 'STAFF_ADMIN',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof StaffUserFormValues>(key: K, value: StaffUserFormValues[K]) =>
    setValues((current) => ({ ...current, [key]: value }));

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onSubmit(values);
      setValues({ email: '', givenName: '', familyName: '', role: 'STAFF_ADMIN' });
    } catch (thrown) {
      setError(
        thrown instanceof ApiError && thrown.issues.length > 0
          ? thrown.issues.map((issue) => `${issue.path}: ${issue.message}`).join(' · ')
          : describeError(t, thrown),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={(event) => void submit(event)} className="space-y-4" noValidate>
      <FormError message={error} />
      <Input
        label={kind === 'owner' ? t('owner.field.email') : t('team.field.email')}
        {...(kind === 'owner' ? { help: t('owner.field.email.help') } : {})}
        type="email"
        autoComplete="off"
        required
        value={values.email}
        onChange={(event) => set('email', event.target.value)}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          label={t('owner.field.givenName')}
          value={values.givenName}
          onChange={(event) => set('givenName', event.target.value)}
        />
        <Input
          label={t('owner.field.familyName')}
          value={values.familyName}
          onChange={(event) => set('familyName', event.target.value)}
        />
      </div>
      {kind === 'staff' ? (
        <Select
          label={t('team.field.role')}
          help={t('team.role.help')}
          value={values.role}
          onChange={(event) => set('role', event.target.value as StaffUserFormValues['role'])}
          options={[
            { value: 'STAFF_ADMIN', label: t('team.role.STAFF_ADMIN') },
            {
              value: 'SUPER_ADMIN',
              label: t('team.role.SUPER_ADMIN'),
              disabled: !canCreateSuperAdmin,
            },
          ]}
        />
      ) : null}
      <div className="flex justify-end pt-2">
        <Button type="submit" loading={busy}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
};
